// galaxy/world.js: the galaxy world (chapter 2, SCALE). Contract notes in ../galaxy.js.
import * as THREE from 'three';
import { makeTarget, Blit, loadTexture, anchor, clamp, smooth, lerp } from '../lib/common.js';
import { buildMaps } from './map.js';
import { makeStars } from './stars.js';
import { makeHIIPoints, makeRingSparks } from './hii.js';
import { Glow } from './glow.js';
import { Pano, makeNear } from './sky.js';
import { evalCamera, makeCamState, applyCamera, BEATS, TUNE } from './camera.js';
import { SUN, rng } from './model.js';

// per-tier budgets: stars, glow resolution (x CSS px), glow steps, dust samples per star, map size
const TIER = {
  high: { stars: 1800000, hii: 2600, res: 0.62, steps: 36, dustK: 3, map: 1024, pano: 'mw-4k.jpg' },
  mid: { stars: 800000, hii: 1600, res: 0.45, steps: 26, dustK: 2, map: 1024, pano: 'mw-2k.jpg' },
  low: { stars: 250000, hii: 900, res: 0.36, steps: 18, dustK: 1, map: 512, pano: 'mw-2k.jpg' },
};
// share of each population's light drawn as resolved stars (the rest is the glow)
const RESOLVED = { old: 0.16, thick: 0.35, bulge: 0.14, young: 0.5 };
const BULGE_K = 0.6 * (1 - 0.5 * RESOLVED.bulge);
const OLD_K = 1 - 0.3 * RESOLVED.old;
// exposure keyframes: [ln distance (kpc), gain]. Rise uses the distance to the Sun,
// the dive the distance to the target. In-plane sight lines are long and bright.
const EV_RISE = [[-9.2, 0.5], [-3.0, 0.5], [-1.2, 0.45], [0.5, 0.47], [2.0, 0.72], [3.7, 1.3]];
const EV_DIVE = [[0.6, 0.08], [1.8, 0.2], [2.6, 0.5], [3.7, 1.3]];
// r7 sky grade (gentle cool white balance, see sky.js) and grade shadow tints: face-on / under the photo / dive end
const SKY = { wb: [0.84, 1.0, 1.1], lift: [0.5, 0.95, 1.7], ch: 1.0, mag: 0.6, liftK: 0.25, sat: 1.1 };
const SH_FACE = [0.1, 0.14, 0.62], SH_SKY = [0.04, 0.08, 0.4], SH_DIVE = [0.05, 0.06, 0.16];
const v3 = (s, d) => { const a = s !== undefined ? String(s).split(',').map(Number) : d; return a.length === 3 && a.every(isFinite) ? a : d; };
const pv = (P, k, d) => (P[k] !== undefined && isFinite(+P[k]) ? +P[k] : d);
// r8: vector params, any of , ; / or space as separator (the harness splits --params on commas: use 'dFar:10/16/.35')
const vv = (s, d) => { const a = s !== undefined ? String(s).split(/[,;/ ]+/).map(Number) : d; return a.length === d.length && a.every(isFinite) ? a : d; };
function keyed(tab, x) {
  if (x <= tab[0][0]) return tab[0][1];
  for (let i = 1; i < tab.length; i++) {
    if (x <= tab[i][0]) { const t = (x - tab[i - 1][0]) / (tab[i][0] - tab[i - 1][0]); return lerp(tab[i - 1][1], tab[i][1], t * t * (3 - 2 * t)); }
  }
  return tab[tab.length - 1][1];
}

export function create(renderer, opts = {}) {
  const tier = TIER[opts.tier] ? opts.tier : 'high';
  const B = TIER[tier];
  const half = opts.half !== false;
  const base = opts.base || '';
  const camera = new THREE.PerspectiveCamera(44, 16 / 9, 1e-5, 500);
  const scene = new THREE.Scene();
  const st = makeCamState();
  const blit = new Blit({ bicubic: true });
  const glow = new Glow({ A: { texture: null }, B: { texture: null } });
  const pano = new Pano();
  let maps = null, stars = null, hii = null, ring = null, near = null, glowRT = null;
  let W = 16, H = 9, dpr = 1, ready = false;
  const anchors = { sun: { x: 0.5, y: 0.5, visible: false }, core: { x: 0.5, y: 0.5, visible: false } };
  // SPECTRUM grade: cool. Void #070B24 from the shadow tint, a faint cool lift in the highlights, no warm gain.
  const grade = { exposure: 1, bloomK: 0.5, threshold: 1.0, sat: 1.35, vignette: 0.3, contrast: 1.05,
    shadows: [0.1, 0.14, 0.62], highs: [-0.03, 0.02, 0.05], gainC: [0.97, 0.99, 1.04] };
  const _c = new THREE.Vector3();
  const dbg = { ev: 1, Ds: 0, D: 0, wP: 0 };
  const dbgOff = { glow: false };
  const tune = { ...TUNE };

  const world = {
    scene, camera, grade, anchors, beats: BEATS, debug: dbg,
    async load() {
      maps = buildMaps(renderer, { size: B.map, half });
      glow.U.uMapA.value = maps.A.texture; glow.U.uMapB.value = maps.B.texture; glow.U.uMapC.value = maps.C.texture;
      glow.U.uSteps.value = B.steps;
      stars = makeStars(B.stars, maps);
      stars.U.uDustK.value = B.dustK;
      stars.setLight(RESOLVED);
      glow.U.uOldK.value = OLD_K; // most resolved light is culled against the glow
      glow.U.uYoungK.value = 1 - RESOLVED.young;
      glow.U.uBulgeK.value = BULGE_K;
      hii = makeHIIPoints(B.hii, maps);
      hii.U.uDustK.value = Math.min(B.dustK, 2);
      ring = makeHIIPoints(0, maps, makeRingSparks(tier === 'low' ? 64 : 160, rng(1097))); // SPECTRUM r7: nuclear-ring hot spots
      ring.U.uDustK.value = 1; ring.U.uNearD.value.set(0.05, 0.15); ring.points.visible = false;
      scene.add(stars.points, hii.points, ring.points);
      const [tex, nr] = await Promise.all([
        loadTexture(`${base}assets/galaxy/${B.pano}`, { srgb: true, renderer }).catch(() => null),
        makeNear(`${base}assets/galaxy/hyg.bin`).catch(() => null),
      ]);
      if (tex) { tex.wrapS = THREE.RepeatWrapping; tex.needsUpdate = true; pano.setTexture(tex); }
      if (nr) { near = nr; scene.add(near.points); }
      ready = true;
    },
    resize(w, h) {
      W = Math.max(1, w); H = Math.max(1, h);
      dpr = renderer.getPixelRatio ? renderer.getPixelRatio() : 1;
      const gw = Math.max(16, Math.round((W / dpr) * B.res)), gh = Math.max(16, Math.round((H / dpr) * B.res));
      if (!glowRT) glowRT = makeTarget(gw, gh, { half });
      else glowRT.setSize(gw, gh);
      camera.aspect = W / H;
      camera.updateProjectionMatrix();
    },
    update(dt, time, input) {
      const p = clamp(input.progress || 0, 0, 1);
      const P = input.params || {};
      for (const k in TUNE) tune[k] = P[k] !== undefined ? +P[k] : TUNE[k]; // debug overrides
      evalCamera(p, input.aspect || W / H, input.look, st, tune);
      camera.aspect = W / H;
      applyCamera(camera, st, +P.shiftX || 0, +P.shiftY || 0);
      const Ds = st.Ds;
      const lDs = Math.log(Math.max(Ds, 1e-4));
      // cross-fade: the real sky near the Sun -> the model as we rise
      let wP = 1 - smooth(Math.log(0.045), Math.log(2.0), lDs);
      if (P.mix !== undefined) wP = +P.mix; // debug: force the sky/model cross-fade
      dbgOff.glow = !!+P.noglow; if (stars) stars.points.visible = !+P.nostars;
      const ev = (p <= BEATS.face0 ? keyed(EV_RISE, lDs) : keyed(EV_DIVE, Math.log(st.D))) * (P.ev !== undefined ? +P.ev : 1) * lerp(1, pv(P, 'dEv', 1.0), smooth(BEATS.face1, BEATS.dive1, p)); // SPECTRUM: darker dive foreground
      const near01 = 1 - smooth(Math.log(0.2), Math.log(6), lDs); // 1 while inside the disk near the Sun
      const pixSr = Math.pow((2 * Math.tan((camera.fov * Math.PI) / 360)) / H, 2);
      dbg.ev = ev; dbg.Ds = Ds; dbg.D = st.D; dbg.wP = wP;
      glow.setCamera(camera, glowRT ? glowRT.height : H);
      glow.U.uGain.value = ev * (1 - wP);
      // SPECTRUM dive end: the near disk dims and the lanes thicken so the cream core and the silhouetted lanes read
      const kDv = smooth(BEATS.face1, BEATS.dive1, p);
      const kC = Math.pow(kDv, pv(P, 'dRamp', 3)); // r7: the colour boosts arrive late (no over-coloured hump mid-dive)
      // r8 calm: rise (0 from p .42, face-on untouched) and dive (0 until face1); crisp size-capped sprites + a calmer glow
      const rK = smooth(pv(P, 'rCalI0', 0.1), pv(P, 'rCalI1', 0.18), p) * (1 - smooth(pv(P, 'rCal0', 0.3), pv(P, 'rCal1', 0.42), p)) * pv(P, 'rCalm', 1);
      const kCal = Math.min(1, pv(P, 'dCalR', 1.6) * kDv) * pv(P, 'dCalm', 1), kCap = Math.max(rK, kCal);
      const crisp = (U, k) => { // r8 sprites: crisp, varied, size-capped knots (k = 0: r7)
        const kk = Math.min(1, 2 * k), dv = kCal >= rK; // r8: dive (kCal) or rise (rK) sprite settings
        U.uCapPx.value = lerp(20 * dpr, (dv ? pv(P, 'hCap', 3.0) : pv(P, 'rCap', 3.0)) * dpr, kk); U.uCapFall.value = pv(P, 'hCapF', 0) * kk; U.uCapKeep.value = (dv ? pv(P, 'hKeep', 1.3) : pv(P, 'rKeep', 1.3)) * kk; U.uCapSoft.value = pv(P, 'hCapS', 0.35) * kk;
        U.uGiant.value = lerp(10, pv(P, 'hGiant', 40), kk); U.uEmax.value = lerp(3, dv ? pv(P, 'hEmax', 12) : pv(P, 'rEmax', 12), kk);
        U.uCrisp.value = pv(P, 'hCrisp', 1) * k; U.uHueVar.value = pv(P, 'hHue', 1) * k; U.uLpow.value = lerp(1, dv ? pv(P, 'hLpow', 1.4) : pv(P, 'rLpow', 1.4), k);
      };
      // r7 dive end: less grey old light, more blue young light + pink HII (the arms' colour), darker crisper lanes
      const dOld = P.dOld !== undefined ? +P.dOld : 0.7, dYng = P.dYng !== undefined ? +P.dYng : 4, dDst = P.dDst !== undefined ? +P.dDst : 1.8;
      glow.U.uOldK.value = OLD_K * lerp(1, dOld, kDv);
      glow.U.uYoungK.value = 1 - RESOLVED.young;
      glow.U.uYB.value = lerp(1, lerp(dYng, pv(P, 'dYng8', 3), kCal), kC); // r8: dYng8 as the calm arrives
      glow.U.uDustK.value = lerp(1, dDst, kDv);
      glow.U.uBarK.value = lerp(1, pv(P, 'dBar', 0.7), kDv);
      glow.U.uSplit.value = pv(P, 'dSplit', 1) * kC;
      glow.U.uMagF.value = pv(P, 'dMag', 0) * kDv; // r7: >0 streaks the magnified near field (phone)
      glow.U.uTint.value = pv(P, 'dTint', 1.6) * kC * lerp(1, pv(P, 'dTintK8', 1), kCal); // r8: paler inner disk (less big smooth azure)
      { const c = v3(P.dTintC, [0.66, 1.0, 1.4]); glow.U.uTintC.value.set(c[0], c[1], c[2]); } // inner disk around the cream core: blue-white
      glow.U.uClump.value = pv(P, 'dClump', 1.5); glow.U.uHiiG.value = lerp(pv(P, 'dHiiG', 5), pv(P, 'dHiiG8', 2.5), kCal); // r8: gentler glow HII boost in the calm (the sprites carry the knots)
      { const c = v3(P.dYC, [0.22, 0.6, 1.0]), c8 = vv(P.dYC8, [0.26, 0.42, 1.0]); glow.U.uYC.value.set(lerp(c[0], c8[0], kCal), lerp(c[1], c8[1], kCal), lerp(c[2], c8[2], kCal)); } // dive clusters: blue-white #8FD3FF-ish; r8: a bluer, whiter blue (dYC8) as the calm arrives
      glow.U.uHazeV.value = kDv;
      glow.U.uCalm.value = kCal; // r8 dive glow calm: knot-only HII boost, diffuse HII faint, far fade, HII not x uYB
      glow.U.uHp.value = pv(P, 'dHp', 1); glow.U.uHiiD.value = pv(P, 'dHiiD', 0.6); glow.U.uYBH.value = pv(P, 'dYBH', 1.2); glow.U.uLcMax.value = pv(P, 'dLcMax', 3);
      { const f = vv(P.dFarH, [9, 14]); glow.U.uFarH.value.set(f[0], f[1]); }
      { const n = vv(P.dNearG, [3, 9]); glow.U.uNearG.value.set(n[0], n[1]); }
      glow.U.uNearY.value = pv(P, 'dNearY', 0.5) * kCal; // r8 near calm: map B dims near the camera
      glow.U.uHcut.value = pv(P, 'rHcut', 1) * rK + pv(P, 'dHcut', 0.9) * kCal; // r8: map-B HII fades by footprint (sprites carry the knots)
      { const f = vv(P.hHF, [0.012, 0.04]); glow.U.uHF.value.set(f[0], f[1]); }
      glow.U.uYKnee.value = pv(P, 'rKnee', 1.0) * rK + pv(P, 'dKnee', 4) * kCal; // r8: soft knee on bright map-B light (no big azure regions)
      glow.U.uYDes.value = Math.min(1, pv(P, 'dYDes', 0.35) + (tier === 'low' ? pv(P, 'lYDes', 0.4) : 0)) * kCal + pv(P, 'rYDes', 0.2) * rK; // r8: diffuse young light -> pale blue-white dYW (luminance kept); low tier + lYDes
      glow.U.uHiDes.value = pv(P, 'dHiDes', 1) * kCal; { const l = vv(P.dHiL, [0.02, 0.15]); glow.U.uHiL.value.set(l[0], l[1]); } // r8: bright bluish glow -> white
      { const c = vv(P.dYW, [0.35, 0.45, 1.0]), l = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]; glow.U.uYW.value.set(c[0] / l, c[1] / l, c[2] / l); }
      // SPECTRUM violet haze: disk-lit everywhere, bulge-lit around the core (grows through the dive)
      const hzC = Math.max(0, 1 - pv(P, 'dHazeCut', 0) * kCal); // integrator: default 0 keeps the violet haze in the dive (cutting it pushed azure over 60% at p.82/p1) // r8: the violet haze is off in the calm dive (the glow skips it)
      glow.U.uHaze.value = P.haze !== undefined ? +P.haze : lerp(0.4, 0.03, kDv) * hzC; // r7: the violet fog is cut at the dive end
      glow.U.uHazeB.value = P.hazeB !== undefined ? +P.hazeB : lerp(0.3, 0.04, kDv) * hzC;
      glow.U.uSteps.value = Math.max(8, Math.round(B.steps * lerp(1, pv(P, 'dStepK', 0.85), kCal) * lerp(1, pv(P, 'rStepK', 1), rK))); // r8: fewer glow steps in the calm dive (pays for the r8 per-step terms)
      // artistic: the bulge swells in only as we climb (the real Galactic centre hides behind dust from the Sun)
      glow.U.uBulgeK.value = BULGE_K * (p <= BEATS.face0 ? lerp(0.35, 1, smooth(Math.log(0.6), Math.log(10), lDs)) : 1) * lerp(0.72, pv(P, 'dBulge', 0.55), smooth(BEATS.face1, BEATS.dive1, p)); // SPECTRUM: cream, not blown
      pano.setCamera(camera, H);
      pano.U.uGain.value = (P.skyG !== undefined ? +P.skyG : 1.3) * wP; // r7: brighter star clouds (the cool grade reads dimmer than the old teal)
      { // r7 sky grade (debug overrides: skyWB, skyLift 'r,g,b'; skyCh, skyMag, skyLiftK)
        const U = pano.U, wb = v3(P.skyWB, SKY.wb), lf = v3(P.skyLift, SKY.lift);
        U.uWB.value.set(wb[0], wb[1], wb[2]); U.uLift.value.set(lf[0], lf[1], lf[2]);
        U.uChK.value = P.skyCh !== undefined ? +P.skyCh : SKY.ch; U.uMagK.value = P.skyMag !== undefined ? +P.skyMag : SKY.mag;
        U.uLiftK.value = P.skyLiftK !== undefined ? +P.skyLiftK : SKY.liftK;
      }
      const sMin = Math.max(2.0, 1.9 * dpr);
      if (stars) {
        const U = stars.U;
        U.uCam.value.copy(camera.position);
        U.uPixSr.value = pixSr;
        U.uGain.value = ev * (1 - 0.55 * wP);
        // near the Sun a model star stands for thousands of suns: keep them faint
        U.uEmax.value = lerp(3.0, 0.3, near01);
        U.uHole.value = 1 - smooth(2.0, 6.0, Ds);
        U.uNear.value = Math.max(0.002, st.D * 0.004);
        U.uSizeMin.value = sMin;
        U.uSizeMax.value = 7 * dpr;
        U.uTime.value = time;
        U.uFlow.value = 0;
        // contrast window (star peak / glow): only stars that stand out are drawn (no sand over bright glow)
        const vf = smooth(BEATS.face0 - 0.12, BEATS.face0, p), vd = smooth(BEATS.face1, BEATS.dive1 - 0.1, p);
        U.uVis.value.set(lerp(lerp(1.0, 1.5, vf), 3.0, vd), lerp(lerp(3.5, 5.0, vf), 10.0, vd));
        if (P.visA !== undefined) U.uVis.value.set(+P.visA, +P.visB);
        U.uBg.value = glowRT ? glowRT.texture : null;
        // stars per pixel ~ N / (disk area in px): thin far away
        const dive = smooth(BEATS.face1, BEATS.dive1, p);
        const kD = clamp(Math.pow(9 / Math.max(st.D, 1e-3), 2), 0.12, 1) * lerp(1, 0.5, dive);
        U.uGain.value *= lerp(1, P.dStar !== undefined ? +P.dStar : 0.12, dive);
        const kW = kD * lerp(1, 0.3, wP);
        U.uKeep.value = kW; U.uKeepY.value = Math.sqrt(kW);
      }
      if (hii) {
        const U = hii.U;
        U.uCam.value.copy(camera.position);
        U.uPixAng.value = (2 * Math.tan((camera.fov * Math.PI) / 360)) / H;
        U.uGain.value = ev * (1 - wP) * lerp(3.1, pv(P, 'dHii', 50), kC) * lerp(1, pv(P, 'rHii', 0.6), rK) * pv(P, 'hiiK', 1) * (tier === 'low' ? lerp(1, pv(P, 'lHii', 2.5), kCal) : 1); // r8: dHii (r7 18) for capped crisp knots; rise x rHii; low tier x lHii (900 knots)
        U.uMinPx.value = sMin;
        U.uMaxPx.value = 20 * dpr;
        { const nd = vv(P.dNearD, [2.0, 4.0]); U.uNearD.value.set(lerp(lerp(1, 2.5, kDv), nd[0], kCal), lerp(lerp(3, 5.5, kDv), nd[1], kCal)); } // r7 near fade; r8: capped knots may come closer
        U.uBlue.value = Math.max(pv(P, 'dBlue', 0.45) * smooth(BEATS.face1 + 0.1, BEATS.dive1, p), pv(P, 'rBlue', 0.35) * rK); // r7 dive; r8: + rise sparkles
        crisp(U, kCap);
        { const f = vv(P.dFar, [10, 16, 0.35]); U.uFar.value.set(f[0], f[1], lerp(1, f[2], kCal)); } // r8: far knots fade (no dotted horizon)
      }
      if (ring) { // SPECTRUM r7 dive end: nuclear-ring hot spots (pink HII + blue-white clusters), off until the dive
        const U = ring.U, kR = smooth(BEATS.face1 + 0.1, BEATS.dive1, p);
        U.uCam.value.copy(camera.position);
        U.uPixAng.value = (2 * Math.tan((camera.fov * Math.PI) / 360)) / H;
        U.uGain.value = ev * (1 - wP) * pv(P, 'ringK', 25) * kR; // r8: 120 -> 25 with ringPow 2: a few bright knots, not a neat pink-white ellipse
        crisp(U, kR * pv(P, 'dCalm', 1)); U.uLpow.value = lerp(1, pv(P, 'ringPow', 2), kR * pv(P, 'dCalm', 1));
        U.uMinPx.value = sMin; U.uMaxPx.value = 12 * dpr;
        U.uTauK.value = P.ringTau !== undefined ? +P.ringTau : 0.15;
        ring.points.visible = kR > 0.001;
      }
      if (near) {
        const U = near.U;
        U.uCam.value.copy(camera.position);
        U.uPixSr.value = pixSr;
        U.uFade.value = 1 - smooth(Math.log(0.3), Math.log(3.0), lDs);
        U.uSizeMin.value = sMin;
        U.uSizeMax.value = 16 * dpr;
      }
      grade.contrast = lerp(1.05, 1.2, smooth(BEATS.face1, BEATS.dive1, p));
      grade.bloomK = lerp(0.5, 0.36, smooth(BEATS.face1, BEATS.dive1, p)); // SPECTRUM: less veil over the lanes
      grade.sat = lerp(lerp(1.35, pv(P, 'skySat', SKY.sat), Math.min(wP / 0.5, 1)), lerp(pv(P, 'dSat', 1.5), pv(P, 'dSat8', 1.65), kCal), smooth(BEATS.face1, BEATS.dive1, p)); // r8: dSat8 // r7: haze cut, so full saturation again // SPECTRUM: no lavender wash in the dive
      // SPECTRUM grade shadows: violet-navy night under the ESO photo and late in the dive (spreads the hues); void ~#070B24 face-on
      const wPk = Math.min(wP / 0.5, 1), kSh = smooth(BEATS.face1, BEATS.dive1, p);
      const dSh = v3(P.dSh, SH_DIVE), sSh = v3(P.skySh, SH_SKY); for (let i = 0; i < 3; i++) grade.shadows[i] = lerp(lerp(SH_FACE[i], sSh[i], wPk), dSh[i], kSh); // r7: a slight blue lift under the photo
      anchor(camera, SUN, anchors.sun);
      anchor(camera, _c.set(0, 0, 0), anchors.core);
      anchors.here = anchors.sun;
    },
    render(target) {
      if (!ready || !glowRT) {
        renderer.setRenderTarget(target); renderer.setClearColor(0x000000, 1); renderer.clear();
        return;
      }
      if (dbgOff.glow) { renderer.setRenderTarget(glowRT); renderer.setClearColor(0x000000, 1); renderer.clear(); } else glow.render(renderer, glowRT);
      blit.render(renderer, glowRT, target);
      const ac = renderer.autoClear;
      renderer.autoClear = false;
      renderer.setRenderTarget(target);
      if (pano.U.uGain.value > 0.002 && pano.U.uPan.value) pano.render(renderer);
      renderer.render(scene, camera);
      renderer.autoClear = ac;
    },
    dispose() {
      if (maps) maps.dispose();
      if (stars) stars.dispose();
      if (hii) hii.dispose();
      if (ring) ring.dispose();
      if (near) near.dispose();
      if (glowRT) glowRT.dispose();
      if (pano.U.uPan.value) pano.U.uPan.value.dispose();
      glow.dispose(); pano.dispose(); blit.dispose();
    },
  };
  return world;
}
